import type {CSSProperties} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Avatar, Card, Text} from '@forecast-it/design-system';
import {raisedSurfaceShadow} from '../../../../styles/elevation';
import type {Ticket} from '../../types/ticket';
import type {MentionUser} from '../../hooks/useMentionUsers';
import {PriorityBadge} from '../PriorityBadge';
import {SLABadge} from '../SLABadge';
import {CardKebabMenu} from './CardKebabMenu';

interface Props {
	ticket: Ticket;
	draggable: boolean;
	users?: MentionUser[];
	onAssign?: (ticketId: string, userId: string | null, note?: string) => void;
	onResolve?: (ticketId: string) => void;
	onDelete?: (ticketId: string) => void;
}

interface TicketCardPreviewProps {
	ticket: Ticket;
	interactive?: boolean;
	className?: string;
	style?: CSSProperties;
	ariaDisabled?: boolean;
	title?: string;
	users?: MentionUser[];
	onAssign?: (userId: string | null, note?: string) => void;
	onResolve?: () => void;
	onDelete?: () => void;
	kebabDisabled?: boolean;
}

// The design system's Card owns the card's own chrome — fill, border, radius, padding.
// This wrapper exists for what the Card is not responsible for: the drag transform, the
// hover shadow, and holding the menu above the content.
const Wrapper = styled.div<{$interactive: boolean}>`
	position: relative;
	border-radius: ${p => p.theme.sys.size.borderradius['5']};
	transition: box-shadow 0.15s;
	/* A card on the board is flat until pointed at; the drag preview is already lifted
	   off the board, so it carries the shadow the whole time. */
	${p => !p.$interactive && raisedSurfaceShadow}

	&:hover {
		${raisedSurfaceShadow}
	}

	/* The menu is chrome, not content: it stays out of the card until the card is
	   hovered or the trigger is focused. */
	&:hover [data-kebab] {
		opacity: 1;
	}
`;

const KebabSlot = styled.div`
	position: absolute;
	top: ${p => p.theme.sys.spacing[200]};
	right: ${p => p.theme.sys.spacing[200]};
	z-index: 1;
`;

// The subject is the card's title and gets two lines before it clips. The right-hand
// gutter keeps a long second line clear of the menu trigger above it.
const subjectCss = `
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	padding-right: 20px;
`;

const SubjectLink = styled(Link)`
	${subjectCss}
	color: inherit;
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

const SubjectText = styled.span`
	${subjectCss}
`;

// Card.Body spaces its children generously, which is right for one block of prose and
// wrong for three stacked facts — so the rows are one child of the body with their own
// tighter rhythm.
const Fields = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[200]};
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[300]};
	flex-wrap: wrap;
`;

const Who = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
	min-width: 0;
`;

const DragWrapper = styled.div<{$draggable: boolean}>`
	cursor: ${p => (p.$draggable ? 'grab' : 'default')};
	${p => p.$draggable && '&:active { cursor: grabbing; }'}
`;

/**
 * One ticket, as it reads on the board.
 *
 * The subject leads, because that is what a reader scans a column for; the identifiers
 * follow. Status is deliberately absent — the column the card sits in already says it,
 * and repeating it on every card spends the card's widest row on the one fact the
 * reader cannot be missing.
 */
export function TicketCardPreview({
	ticket,
	interactive = false,
	className,
	style,
	ariaDisabled,
	title,
	users,
	onAssign,
	onResolve,
	onDelete,
	kebabDisabled = false,
}: TicketCardPreviewProps) {
	return (
		<Wrapper
			className={className}
			style={style}
			role="article"
			aria-disabled={ariaDisabled}
			title={title}
			$interactive={interactive}
		>
			{interactive && users && onAssign && (
				<KebabSlot>
					<CardKebabMenu
						assigneeId={ticket.assignee_id}
						users={users}
						onAssign={onAssign}
						onResolve={onResolve}
						onDelete={onDelete}
						disabled={kebabDisabled}
					/>
				</KebabSlot>
			)}

			<Card width="100%">
				<Card.Title>
					{interactive ? (
						<SubjectLink
							to={`/tickets/${ticket.slug}`}
							// Stop drag from intercepting the click. Without this, dnd-kit's pointer
							// sensor swallows the navigation.
							onPointerDownCapture={e => e.stopPropagation()}
						>
							{ticket.subject}
						</SubjectLink>
					) : (
						<SubjectText>{ticket.subject}</SubjectText>
					)}
				</Card.Title>
				<Card.Body>
					<Fields>
						<Row>
							<Text size="2" color="auxiliary">
								{ticket.slug}
							</Text>
							<PriorityBadge priority={ticket.priority} />
						</Row>

						{ticket.requester_name && (
							<Who>
								<Avatar name={ticket.requester_name} size="s" />
								<Text size="2">{ticket.requester_name}</Text>
							</Who>
						)}

						<Row>
							<Who>
								<Avatar name={ticket.assignee_name ?? 'Unassigned'} size="s" />
								<Text size="2" color="auxiliary">
									{ticket.assignee_id && ticket.assignee_name ? ticket.assignee_name : 'Unassigned'}
								</Text>
							</Who>
							<SLABadge ticket={ticket} compact />
						</Row>
					</Fields>
				</Card.Body>
			</Card>
		</Wrapper>
	);
}

export function TicketCard({ticket, draggable, users, onAssign, onResolve, onDelete}: Props) {
	const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
		id: ticket.id,
		disabled: !draggable,
	});

	const style: CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const dragProps = draggable ? {...attributes, ...listeners} : {};

	return (
		<DragWrapper ref={setNodeRef} style={style} $draggable={draggable} {...dragProps}>
			<TicketCardPreview
				ticket={ticket}
				interactive
				ariaDisabled={!draggable}
				title={draggable ? undefined : 'You can only move tickets assigned to you.'}
				users={users}
				onAssign={onAssign ? (userId, note) => onAssign(ticket.id, userId, note) : undefined}
				onResolve={onResolve ? () => onResolve(ticket.id) : undefined}
				onDelete={onDelete ? () => onDelete(ticket.globalId) : undefined}
				kebabDisabled={!draggable}
			/>
		</DragWrapper>
	);
}
